import OrganizationPageCategorySection from 'components/Organizations/OrganizationPageCategorySection';
import SeoWrapper from 'components/Seo/SeoWrapper';
import BBreadcrumb from 'components/common/BBreadcrumb';
import { Locale } from 'enums/locale';
import { checkAuthentication } from 'guards/checkAuthentication';
import { useBreadcrumbs } from 'hooks/useBreadcrumbs';
import { getSeoData } from 'lib/seo/seoData';
import type { GetServerSideProps, NextPage } from 'next';
import useTranslation from 'next-translate/useTranslation';
import { organizationService } from 'services/OrganizationService';
import { OrganizationByCategoryResponse } from 'types/organization.type';
import { SeoProps } from 'types/seo.type';

export const getServerSideProps: GetServerSideProps = checkAuthentication(
  async (ctx) => {
    const organizationsByCategoryPromise =
      organizationService.getCategorizedOrganizations(ctx);

    const seoPath = ctx.locale === Locale.TR ? `/hizmetler` : `/services`;
    const originalPath = `${ctx.locale === Locale.TR ? '' : '/en'}${seoPath}`;
    const seoPromise = getSeoData(seoPath, ctx.locale as Locale, originalPath);

    const [organizationsByCategory, seoData] = await Promise.allSettled([
      organizationsByCategoryPromise,
      seoPromise,
    ]);

    return {
      props: {
        ...(organizationsByCategory.status === 'fulfilled'
          ? { organizationsByCategory: organizationsByCategory.value }
          : {}),
        ...(seoData.status === 'fulfilled' ? { ...seoData.value } : {}),
      },
    };
  }
);

interface OrganizationsPageProps extends SeoProps {
  organizationsByCategory: OrganizationByCategoryResponse;
}

const Organizations: NextPage<OrganizationsPageProps> = ({
  organizationsByCategory,
  content,
  helmetSettings,
}) => {
  const { t } = useTranslation('organizations');
  const { generateOrganizationBreadcrumbs } = useBreadcrumbs();

  const breadcrumbs = generateOrganizationBreadcrumbs();

  return (
    <SeoWrapper content={content} helmetSettings={helmetSettings}>
      <BBreadcrumb breadcrumbItems={breadcrumbs} wrapperClasses="mb-4" />
      <h1 className="text-semibold mb-6 text-2xl">{t('home.title')}</h1>
      <OrganizationPageCategorySection
        title={t('home.popularOrganizations.title')}
        organizations={organizationsByCategory.POPULAR}
        isCarousel
        keyName="populer-organizations"
        wrapperClass="mb-12"
        prioritizeImg
      />
      <OrganizationPageCategorySection
        title={t('home.corporateOrganizations.title')}
        organizations={organizationsByCategory.BUSINESS}
        isCarousel
        keyName="corporate-organizations"
        wrapperClass="mb-12"
      />
      <OrganizationPageCategorySection
        title={t('home.otherOrganizations.title')}
        organizations={organizationsByCategory.OTHER}
        keyName="other-organizations"
      />
    </SeoWrapper>
  );
};

export default Organizations;
